import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
    Loader2, Search, Download, Users, FileText, Activity, Brain, Clock,
    Eye, ChevronLeft, ChevronRight, ShieldAlert, TrendingUp, TrendingDown,
    RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SUPERADMIN_EMAILS = new Set([
    'alvaro.mardones.rivera@gmail.com',
    'mariuerosima@gmail.com',
    'acontistaeduca@gmail.com',
]);

function fmt(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-CL', {
            day: '2-digit', month: 'short', year: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return '—'; }
}
function fmtDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('es-CL', {
            day: '2-digit', month: 'short', year: '2-digit',
        });
    } catch { return '—'; }
}

function StatCard({ title, value, icon: Icon, hint, tone = 'slate' }) {
    const tones = {
        slate: 'bg-slate-100 text-slate-700',
        indigo: 'bg-indigo-100 text-indigo-700',
        emerald: 'bg-emerald-100 text-emerald-700',
        amber: 'bg-amber-100 text-amber-700',
        rose: 'bg-rose-100 text-rose-700',
    };
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bmon-stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-xs font-medium text-slate-500">{title}</div>
                    <div className="mt-1 font-mono text-2xl font-bold text-slate-900">{value}</div>
                    {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
                </div>
                <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone] || tones.slate}`}>
                    <Icon className="h-4 w-4" />
                </div>
            </div>
        </div>
    );
}

function DraftDetailDialog({ draftId, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !draftId) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/borradores/drafts/${draftId}`);
                setData(data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'No se pudo cargar el borrador');
            } finally { setLoading(false); }
        })();
    }, [draftId, open]);

    const d = data?.draft;
    const u = data?.user;
    const fb = d?.feedback;
    const log = (d?.activity_log || []).slice().reverse();

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bmon-draft-detail">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Borrador · Vista de sólo lectura
                    </DialogTitle>
                    <DialogDescription>
                        Este contenido se muestra <strong>exclusivamente para soporte, investigación pedagógica y mejora continua</strong>.
                        Los administradores <strong>no modifican</strong> el trabajo realizado por los docentes.
                    </DialogDescription>
                </DialogHeader>
                {loading || !d ? (
                    <div className="flex h-64 items-center justify-center text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando borrador…
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 grid grid-cols-2 md:grid-cols-4 gap-2">
                            <div><span className="text-slate-500">Docente:</span> <strong>{u?.nombre || '—'}</strong></div>
                            <div><span className="text-slate-500">Email:</span> <span className="font-mono">{u?.email || '—'}</span></div>
                            <div><span className="text-slate-500">Establecimiento:</span> {u?.perfil?.establecimiento_nombre || u?.perfil?.establecimiento || '—'}</div>
                            <div><span className="text-slate-500">Nivel perfil:</span> {u?.perfil?.nivel || '—'}</div>
                            <div><span className="text-slate-500">Tarea:</span> <strong>{d.tarea || '—'}</strong></div>
                            <div><span className="text-slate-500">Indicador:</span> <strong>{d.indicador || '—'}</strong> {d.indicador_id ? <span className="text-slate-400">({d.indicador_id})</span> : null}</div>
                            <div><span className="text-slate-500">Nivel estimado IA:</span> <Badge variant="outline">{d.nivel_estimado || '—'}</Badge></div>
                            <div><span className="text-slate-500">Dificultad:</span> {d.dificultad || '—'}</div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <StatCard title="Analizar con IA" value={d.ai_analysis_count || 0} icon={Brain} hint="veces" tone="indigo" />
                            <StatCard title="Ediciones" value={d.edit_count || 0} icon={Activity} hint="guardados" tone="emerald" />
                            <StatCard title="Autoguardados" value={d.autosave_count || 0} icon={RefreshCw} hint="beacons" tone="slate" />
                            <StatCard title="Tiempo activo" value={`${d.active_time_min || 0} min`} icon={Clock} hint="estimado" tone="amber" />
                        </div>

                        <div>
                            <h4 className="font-heading text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Contenido del borrador</h4>
                            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800 whitespace-pre-wrap max-h-[40vh] overflow-y-auto" data-testid="bmon-draft-content">
                                {d.contenido || <em className="text-slate-400">(vacío)</em>}
                            </div>
                        </div>

                        {fb && (
                            <div>
                                <h4 className="font-heading text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5"><Brain className="h-3.5 w-3.5" /> Feedback IA</h4>
                                <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-xs space-y-2">
                                    {fb.nivel_estimado && <div><strong>Nivel estimado:</strong> {fb.nivel_estimado}</div>}
                                    {fb.justificacion_nivel && <div><strong>Justificación:</strong> {fb.justificacion_nivel}</div>}
                                    {Array.isArray(fb.fortalezas) && fb.fortalezas.length > 0 && (
                                        <div><strong>Fortalezas:</strong>
                                            <ul className="ml-4 list-disc mt-0.5">{fb.fortalezas.map((x, i) => <li key={i}>{x}</li>)}</ul>
                                        </div>
                                    )}
                                    {Array.isArray(fb.brechas) && fb.brechas.length > 0 && (
                                        <div><strong>Brechas:</strong>
                                            <ul className="ml-4 list-disc mt-0.5">{fb.brechas.map((x, i) => <li key={i}>{x}</li>)}</ul>
                                        </div>
                                    )}
                                    {Array.isArray(fb.mejoras) && fb.mejoras.length > 0 && (
                                        <div><strong>Mejoras sugeridas:</strong>
                                            <ul className="ml-4 list-disc mt-0.5">{fb.mejoras.map((x, i) => <li key={i}>{x}</li>)}</ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div>
                            <h4 className="font-heading text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" /> Historial</h4>
                            <div className="rounded-lg border border-slate-200 bg-white max-h-56 overflow-y-auto text-xs">
                                {log.length === 0 ? (
                                    <div className="p-3 text-slate-400 italic">Sin eventos registrados aún.</div>
                                ) : (
                                    <ul className="divide-y divide-slate-100">
                                        {log.map((ev, i) => (
                                            <li key={i} className="p-2 flex items-center justify-between gap-2">
                                                <span className="font-mono text-slate-500">{fmt(ev.at)}</span>
                                                <Badge variant="outline" className="text-[10px]">
                                                    {ev.type === 'created' ? '📄 Creado'
                                                        : ev.type === 'edit' ? '✏️ Edición'
                                                            : ev.type === 'ai_analysis' ? `🧠 Análisis IA${ev.nivel ? ` · ${ev.nivel}` : ''}`
                                                                : ev.type}
                                                </Badge>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="mt-1.5 text-[11px] text-slate-400">
                                Creado {fmt(d.created_at)} · Modificado {fmt(d.updated_at)} · Autoguardado {fmt(d.auto_saved_at)}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function UserDrawer({ user, open, onClose, onOpenDraft }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!open || !user) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/admin/borradores/users/${user.user_id}/drafts`);
                setData(data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || 'No se pudo cargar el detalle');
            } finally { setLoading(false); }
        })();
    }, [user, open]);
    if (!user) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="bmon-user-drawer">
                <DialogHeader>
                    <DialogTitle className="font-heading">
                        {user.nombre || 'Sin nombre'} — <span className="font-mono text-sm text-slate-500">{user.email}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Registro: {fmtDate(user.created_at)} · Establecimiento: <strong>{user.establecimiento || '—'}</strong>
                        {user.comuna ? ` · ${user.comuna}` : ''}{user.region ? ` · ${user.region}` : ''}
                    </DialogDescription>
                </DialogHeader>
                {loading || !data ? (
                    <div className="flex h-64 items-center justify-center text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando borradores…
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto space-y-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <StatCard title="Borradores" value={user.drafts} icon={FileText} tone="slate" />
                            <StatCard title="Ediciones" value={user.edits_total} icon={Activity} tone="emerald" />
                            <StatCard title="Análisis IA" value={user.ai_total} icon={Brain} tone="indigo" />
                            <StatCard title="Última actividad" value={fmtDate(user.last_activity_at)} icon={Clock} tone={user.active_7d ? 'emerald' : 'slate'} />
                        </div>
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                                    <tr>
                                        <th className="px-2 py-2 text-left">Indicador</th>
                                        <th className="px-2 py-2 text-left">Tarea</th>
                                        <th className="px-2 py-2 text-center">Nivel IA</th>
                                        <th className="px-2 py-2 text-center">Analizar IA</th>
                                        <th className="px-2 py-2 text-center">Ediciones</th>
                                        <th className="px-2 py-2 text-center">Tiempo activo</th>
                                        <th className="px-2 py-2 text-left">Actualizado</th>
                                        <th className="px-2 py-2 text-left">Creado</th>
                                        <th className="w-8"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.drafts.map((d) => (
                                        <tr key={d.id} className="border-t border-slate-100" data-testid={`bmon-draft-row-${d.id}`}>
                                            <td className="px-2 py-1.5 text-slate-800 max-w-[280px] truncate" title={d.indicador}>{d.indicador || '—'}</td>
                                            <td className="px-2 py-1.5 text-slate-600">{d.tarea || '—'}</td>
                                            <td className="px-2 py-1.5 text-center"><Badge variant="outline" className="text-[10px]">{d.nivel_estimado}</Badge></td>
                                            <td className="px-2 py-1.5 text-center font-mono font-bold text-indigo-700">{d.ai_analysis_count}</td>
                                            <td className="px-2 py-1.5 text-center font-mono text-slate-700">{d.edit_count}</td>
                                            <td className="px-2 py-1.5 text-center text-slate-600">{d.active_time_min} min</td>
                                            <td className="px-2 py-1.5 text-slate-500">{fmt(d.updated_at)}</td>
                                            <td className="px-2 py-1.5 text-slate-500">{fmt(d.created_at)}</td>
                                            <td className="px-2 py-1.5 text-center">
                                                <button
                                                    onClick={() => onOpenDraft(d.id)}
                                                    className="text-slate-500 hover:text-slate-900"
                                                    aria-label="Abrir borrador"
                                                    data-testid={`bmon-open-draft-${d.id}`}
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {data.drafts.length === 0 && (
                                        <tr><td colSpan={9} className="p-4 text-center text-slate-400 italic">Sin borradores.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function BorradoresMonitorPanel({ currentEmail }) {
    const authorized = SUPERADMIN_EMAILS.has((currentEmail || '').toLowerCase());
    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [perPage] = useState(25);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [q, setQ] = useState('');
    const [establecimiento, setEstablecimiento] = useState('');
    const [status, setStatus] = useState('all');
    const [minDrafts, setMinDrafts] = useState(0);
    const [minAi, setMinAi] = useState(0);
    const [sort, setSort] = useState('last_activity');
    const [openUser, setOpenUser] = useState(null);
    const [openDraftId, setOpenDraftId] = useState(null);
    const [exporting, setExporting] = useState(false);
    const searchTimer = useRef(null);

    const loadOverview = useCallback(async () => {
        setLoadingOverview(true);
        try {
            const { data } = await api.get('/admin/borradores/overview');
            setOverview(data);
        } catch (e) {
            if (e?.response?.status !== 403) toast.error('No se pudo cargar el resumen');
        } finally { setLoadingOverview(false); }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const { data } = await api.get('/admin/borradores/users', {
                params: { page, per_page: perPage, q, establecimiento, status, min_drafts: minDrafts, min_ai: minAi, sort },
            });
            setUsers(data.users || []);
            setTotal(data.total || 0);
            setTotalPages(data.total_pages || 1);
        } catch (e) {
            if (e?.response?.status !== 403) toast.error('No se pudo cargar la lista');
        } finally { setLoadingUsers(false); }
    }, [page, perPage, q, establecimiento, status, minDrafts, minAi, sort]);

    useEffect(() => { if (authorized) { loadOverview(); } }, [authorized, loadOverview]);
    useEffect(() => { if (authorized) { loadUsers(); } }, [authorized, loadUsers]);

    const onSearchChange = (v) => {
        setQ(v);
        setPage(1);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => { /* effect ya dispara vía loadUsers */ }, 250);
    };

    const handleExport = async (format, detailed = false) => {
        setExporting(true);
        try {
            const res = await api.get('/admin/borradores/export', {
                params: { format, detailed },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `borradores_stats.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Exportado como ${format.toUpperCase()}`);
        } catch (e) {
            toast.error('Error al exportar');
        } finally { setExporting(false); }
    };

    const kpis = useMemo(() => (overview ? [
        { title: 'Usuarios con borradores', value: overview.users_with_drafts, icon: Users, tone: 'indigo' },
        { title: 'Activos hoy', value: overview.active_today, icon: Activity, tone: 'emerald' },
        { title: 'Activos esta semana', value: overview.active_week, icon: TrendingUp, tone: 'emerald' },
        { title: 'Borradores totales', value: overview.total_drafts, icon: FileText, tone: 'slate' },
        { title: 'Análisis IA totales', value: overview.total_ai_analyses, icon: Brain, tone: 'indigo' },
        { title: 'Ediciones totales', value: overview.total_edits, icon: Activity, tone: 'slate' },
        { title: 'Borradores por usuario', value: overview.avg_drafts_per_user, icon: FileText, tone: 'slate', hint: 'promedio' },
        { title: 'IA por usuario', value: overview.avg_ai_per_user, icon: Brain, tone: 'indigo', hint: 'promedio' },
        { title: 'IA por borrador', value: overview.avg_ai_per_draft, icon: Brain, tone: 'indigo', hint: 'promedio' },
        { title: 'Nunca usaron IA', value: overview.users_never_used_ai, icon: TrendingDown, tone: 'rose' },
        { title: 'Uso intensivo IA', value: overview.heavy_ai_users, icon: TrendingUp, tone: 'amber', hint: `≥${overview.heavy_ai_threshold} análisis` },
    ] : []), [overview]);

    if (!authorized) {
        return (
            <Card className="border-rose-200 bg-rose-50/40" data-testid="bmon-forbidden">
                <CardContent className="p-6 flex items-start gap-3">
                    <ShieldAlert className="h-5 w-5 text-rose-600 mt-0.5" />
                    <div>
                        <h3 className="font-heading text-base font-semibold text-rose-900">Acceso restringido</h3>
                        <p className="text-sm text-rose-800 mt-1">
                            Este módulo es exclusivo para 3 administradores autorizados. Contacta al equipo si necesitas acceso.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="borradores-monitor-panel">
            <CardHeader>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                            <FileText className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="font-heading text-lg">Monitor de Borradores de Usuarios</CardTitle>
                            <CardDescription>
                                Observatorio de uso real (sólo lectura). Detecta dificultades, mejora la experiencia
                                y comprende el uso de la IA por parte de la comunidad docente.
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => { loadOverview(); loadUsers(); }} className="gap-1.5" data-testid="bmon-refresh">
                            <RefreshCw className="h-3.5 w-3.5" /> Refrescar
                        </Button>
                        <Button size="sm" onClick={() => handleExport('csv', false)} disabled={exporting} className="gap-1.5" data-testid="bmon-export-csv">
                            <Download className="h-3.5 w-3.5" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleExport('xlsx', true)} disabled={exporting} className="gap-1.5" data-testid="bmon-export-xlsx">
                            <Download className="h-3.5 w-3.5" /> Excel detallado
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Aviso de privacidad */}
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="bmon-privacy-notice">
                    <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <p>
                        Esta sección es exclusivamente para fines de soporte, investigación pedagógica y mejora continua de
                        la plataforma. Los administradores <strong>no modifican</strong> el trabajo realizado por los docentes.
                        Toda la vista es de <strong>sólo lectura</strong>.
                    </p>
                </div>

                {/* KPIs */}
                {loadingOverview ? (
                    <div className="flex h-24 items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando resumen…</div>
                ) : overview && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                            {kpis.map((k) => (
                                <StatCard key={k.title} title={k.title} value={k.value} icon={k.icon} tone={k.tone} hint={k.hint} />
                            ))}
                        </div>

                        {/* Indicadores más y menos trabajados */}
                        <div className="grid md:grid-cols-2 gap-3">
                            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="bmon-top-indicators">
                                <h4 className="font-heading text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Indicadores más trabajados</h4>
                                <ul className="text-xs space-y-1">
                                    {overview.top_indicators.slice(0, 8).map((r, i) => (
                                        <li key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1 last:border-0">
                                            <span className="text-slate-800 truncate">{r.indicador || '—'}</span>
                                            <span className="text-slate-500 font-mono whitespace-nowrap">{r.drafts} borr · {r.ai_analyses} IA</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="bmon-bottom-indicators">
                                <h4 className="font-heading text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-1.5"><TrendingDown className="h-3.5 w-3.5 text-rose-600" /> Indicadores menos trabajados</h4>
                                <ul className="text-xs space-y-1">
                                    {overview.bottom_indicators.slice(0, 8).map((r, i) => (
                                        <li key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 py-1 last:border-0">
                                            <span className="text-slate-800 truncate">{r.indicador || '—'}</span>
                                            <span className="text-slate-500 font-mono whitespace-nowrap">{r.drafts} borr · {r.ai_analyses} IA</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </>
                )}

                {/* Filtros */}
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                            <Search className="h-4 w-4 text-slate-400" />
                            <Input value={q} onChange={(e) => { setPage(1); onSearchChange(e.target.value); }} placeholder="Buscar por nombre o email…" className="h-8" data-testid="bmon-search" />
                        </div>
                        <Input value={establecimiento} onChange={(e) => { setPage(1); setEstablecimiento(e.target.value); }} placeholder="Establecimiento…" className="h-8 w-56" data-testid="bmon-filter-establecimiento" />
                        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="bmon-filter-status">
                            <option value="all">Todos</option>
                            <option value="active7d">Activos (7 días)</option>
                            <option value="inactive7d">Inactivos con borradores</option>
                            <option value="no_drafts">Sin borradores</option>
                        </select>
                        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="bmon-sort">
                            <option value="last_activity">Última actividad</option>
                            <option value="total_ai">Más análisis IA</option>
                            <option value="total_drafts">Más borradores</option>
                            <option value="created_at">Registro (recientes)</option>
                            <option value="name">Nombre</option>
                        </select>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                        <label className="flex items-center gap-1">Mín borradores: <input type="number" min="0" value={minDrafts} onChange={(e) => { setPage(1); setMinDrafts(Number(e.target.value) || 0); }} className="w-16 h-7 rounded border border-slate-300 px-1.5 font-mono" data-testid="bmon-filter-min-drafts" /></label>
                        <label className="flex items-center gap-1">Mín IA: <input type="number" min="0" value={minAi} onChange={(e) => { setPage(1); setMinAi(Number(e.target.value) || 0); }} className="w-16 h-7 rounded border border-slate-300 px-1.5 font-mono" data-testid="bmon-filter-min-ai" /></label>
                        <span className="ml-auto text-slate-500">{total} usuario{total === 1 ? '' : 's'} · página {page}/{totalPages}</span>
                    </div>
                </div>

                {/* Tabla usuarios */}
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm" data-testid="bmon-users-table">
                        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                            <tr>
                                <th className="px-3 py-2 text-left">Docente</th>
                                <th className="px-3 py-2 text-left">Establecimiento</th>
                                <th className="px-3 py-2 text-left">Registro</th>
                                <th className="px-3 py-2 text-left">Última actividad</th>
                                <th className="px-3 py-2 text-center">Borr.</th>
                                <th className="px-3 py-2 text-center">Análisis IA</th>
                                <th className="px-3 py-2 text-center">Ediciones</th>
                                <th className="px-3 py-2 text-center">7d</th>
                                <th className="w-10"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingUsers ? (
                                <tr><td colSpan={9} className="p-8 text-center text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan={9} className="p-8 text-center text-slate-400 italic">Sin resultados con los filtros actuales.</td></tr>
                            ) : users.map((u) => (
                                <tr
                                    key={u.user_id}
                                    className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer"
                                    onClick={() => setOpenUser(u)}
                                    data-testid={`bmon-user-row-${u.user_id}`}
                                >
                                    <td className="px-3 py-2">
                                        <div className="text-slate-900 font-medium">{u.nombre || '—'}</div>
                                        <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[220px] truncate" title={u.establecimiento}>
                                        {u.establecimiento || '—'}
                                        {u.comuna || u.region ? <div className="text-slate-400">{[u.comuna, u.region].filter(Boolean).join(' · ')}</div> : null}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{fmt(u.last_activity_at)}</td>
                                    <td className="px-3 py-2 text-center font-mono font-bold text-slate-800">{u.drafts}</td>
                                    <td className="px-3 py-2 text-center font-mono font-bold text-indigo-700">{u.ai_total}</td>
                                    <td className="px-3 py-2 text-center font-mono text-slate-700">{u.edits_total}</td>
                                    <td className="px-3 py-2 text-center">
                                        {u.active_7d
                                            ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Activo</Badge>
                                            : <Badge variant="outline" className="text-[10px]">Inactivo</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 text-center"><Eye className="h-3.5 w-3.5 inline" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between text-sm">
                        <div className="text-slate-500">Mostrando {Math.min((page - 1) * perPage + 1, total)}–{Math.min(page * perPage, total)} de {total}</div>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="bmon-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
                            <span className="px-2 font-mono text-slate-700">{page} / {totalPages}</span>
                            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="bmon-page-next"><ChevronRight className="h-4 w-4" /></Button>
                        </div>
                    </div>
                )}
            </CardContent>

            <UserDrawer
                user={openUser}
                open={!!openUser}
                onClose={() => setOpenUser(null)}
                onOpenDraft={(id) => setOpenDraftId(id)}
            />
            <DraftDetailDialog
                draftId={openDraftId}
                open={!!openDraftId}
                onClose={() => setOpenDraftId(null)}
            />
        </Card>
    );
}
